/**
 * REPORTS — the trend page.
 *
 * WHAT THIS PAGE IS FOR. An operator arrives with a trend question: is this provider getting
 * slower, did the inbound leg degrade after Tuesday. A table of percentiles cannot answer
 * either, because a table has no time axis and no way to show one leg moving while the other
 * holds. The table is kept below the charts — it is the exact-value view and the text
 * equivalent of what the charts draw — but it is no longer the whole page.
 *
 * WHERE THE DATA COMES FROM, AND WHY NOT FROM THE TIME-SERIES ENDPOINT. All three charts read
 * `GET /api/runs` (`api.listAllRuns`), because per-leg bands need `t_fax_sent` and the quality
 * trend needs `quality_score`, and `GET /api/reports/job/{id}/timeseries` serves NEITHER — it
 * publishes a combined `e2e_seconds` and the `quality_result` readability enum. One windowed
 * `/api/runs` read also covers every job at once, where the time-series endpoint is one request
 * per job. `api.jobTimeseries` was therefore removed rather than left exported with no caller;
 * the backend endpoint is untouched.
 *
 * ARITHMETIC LIVES IN `reportsChartData.ts`, not here. That module is pure and is executed by
 * tests/test_reports_charts_gap_and_grouping.py under node, which is how the gap rule below is
 * proved rather than asserted about the source text.
 *
 * THE GAP RULE. A chart must never imply data it does not have. Empty buckets are emitted so a
 * quiet period occupies its real width, their values are `null` and never `0`, the quality line
 * sets `connectNulls={false}` so it BREAKS across a gap instead of drawing a straight segment
 * across it, and a bucket with no runs simply has no bar. `0` is reserved for its true meaning:
 * a real, reachable, sub-second measurement.
 *
 * ACCESSIBILITY. Colour is never the only carrier of meaning. The two legs differ in hue AND
 * luminance (Okabe-Ito, chosen against red/green precisely because a red/green split is
 * invisible to a substantial minority of operators), the inbound leg is additionally HATCHED
 * and its line DASHED, each group gets its own hatch angle, every series is named in words in
 * the legend and in the tooltip, each chart carries a `role="img"` summary for a screen reader,
 * and the table beneath carries the numbers. Every colour comes from `theme.chart`; there are
 * no hexes in this file.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api, Job, JobStats, Run } from '../api/client'
import { theme } from '../theme'
import { fmtDate, fmtDateTime, fmtTime } from '../utils/datetime'
import { providerLabel } from '../utils/methods'
import {
  ALL_GROUP,
  ChartFilters,
  GroupBy,
  NOT_RECORDED,
  bucketSizeMs,
  buildLegDistribution,
  buildQualityTrend,
  buildTimeBands,
  methodOptions,
  providerOptions,
} from './reportsChartData'

/**
 * How much of the window the charts will pull.
 *
 * `GET /api/runs` caps `page_size` at 200, so a long window is several requests. This bound
 * exists so a 30-day window on a busy deployment cannot issue hundreds of them. When it bites,
 * the page SAYS SO above the charts — a silently truncated chart is the exact failure this file
 * is otherwise built to avoid. A server-side grouped aggregation would remove the bound
 * entirely; see the note in the report accompanying this change.
 */
const PAGE_SIZE = 200
const MAX_PAGES = 25

/** Groups drawn at once. Past this the chart is unreadable, so it says what it left out. */
const MAX_GROUPS = 6

/** Hatch angles, the per-group non-colour carrier. Index 0 is unhatched: the simplest reading. */
const HATCH_ANGLES = [null, 45, -45, 90, 20, -20] as const

interface LoadedWindow {
  runs: Run[]
  total: number
  fetched: number
  truncated: boolean
}

export default function Reports() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [stats, setStats] = useState<Record<string, JobStats>>({})
  const [hours, setHours] = useState(24)
  const [loading, setLoading] = useState(true)
  const [runsWindow, setRunsWindow] = useState<LoadedWindow>({ runs: [], total: 0, fetched: 0, truncated: false })
  const [runsError, setRunsError] = useState<string | null>(null)

  const [groupBy, setGroupBy] = useState<GroupBy>('none')
  const [filters, setFilters] = useState<ChartFilters>({ provider: '', method: '' })

  // The window is computed ONCE per load and reused by every chart, so the three of them and
  // the table below cannot describe different periods.
  const [windowBounds, setWindowBounds] = useState(() => {
    const toMs = Date.now()
    return { fromMs: toMs - 24 * 3600_000, toMs }
  })

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setRunsError(null)

    const toMs = Date.now()
    const fromMs = toMs - hours * 3600_000
    setWindowBounds({ fromMs, toMs })

    const load = async () => {
      const jobList = await api.listJobs(true)
      if (cancelled) return
      setJobs(jobList)

      const statsMap: Record<string, JobStats> = {}
      await Promise.all(
        jobList.map(async job => {
          try {
            statsMap[job.id] = await api.jobStats(job.id, hours)
          } catch { /* a job with no samples is not an error */ }
        }),
      )
      if (cancelled) return
      setStats(statsMap)

      // Runs for the charts. Paged, bounded, and honest about the bound.
      try {
        const from = new Date(fromMs).toISOString()
        const to = new Date(toMs).toISOString()
        const collected: Run[] = []
        let total = 0
        let truncated = false
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const result = await api.listAllRuns({ from, to, page, page_size: PAGE_SIZE })
          if (cancelled) return
          total = result.total
          collected.push(...result.items)
          if (page >= result.total_pages || result.items.length === 0) break
          if (page === MAX_PAGES && result.total_pages > MAX_PAGES) truncated = true
        }
        setRunsWindow({ runs: collected, total, fetched: collected.length, truncated })
      } catch (err) {
        setRunsWindow({ runs: [], total: 0, fetched: 0, truncated: false })
        setRunsError(err instanceof Error ? err.message : String(err))
      }
      if (!cancelled) setLoading(false)
    }

    load().catch(err => {
      if (cancelled) return
      setRunsError(err instanceof Error ? err.message : String(err))
      setLoading(false)
    })

    return () => { cancelled = true }
  }, [hours])

  const window = useMemo(
    () => ({ fromMs: windowBounds.fromMs, toMs: windowBounds.toMs, hours }),
    [windowBounds, hours],
  )

  const bands = useMemo(
    () => buildTimeBands(runsWindow.runs, jobs, window, groupBy, filters),
    [runsWindow.runs, jobs, window, groupBy, filters],
  )
  const distribution = useMemo(
    () => buildLegDistribution(runsWindow.runs, jobs, groupBy, filters),
    [runsWindow.runs, jobs, groupBy, filters],
  )
  const quality = useMemo(
    () => buildQualityTrend(runsWindow.runs, jobs, window, groupBy, filters),
    [runsWindow.runs, jobs, window, groupBy, filters],
  )

  const shownGroups = bands.groups.slice(0, MAX_GROUPS)
  const hiddenGroupCount = bands.groups.length - shownGroups.length
  const bucketMs = bucketSizeMs(hours)

  if (loading) return <div style={{ color: theme.text }}>Loading reports...</div>

  return (
    <div>
      <div style={headerRowStyle}>
        <h2 style={{ margin: 0 }}>Reports</h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <label style={srOnlyLabel} htmlFor="reports-window">Time window</label>
          <select
            id="reports-window"
            value={hours}
            onChange={e => setHours(Number(e.target.value))}
            style={selectStyle}
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <button onClick={() => api.exportCsv(hours)} style={buttonStyle}>Export CSV</button>
        </div>
      </div>

      <ChartControls
        jobs={jobs}
        groupBy={groupBy}
        onGroupBy={setGroupBy}
        filters={filters}
        onFilters={setFilters}
      />

      <DataScopeNotice
        window={runsWindow}
        runsError={runsError}
        emptyBuckets={bands.emptyBuckets}
        bucketCount={bands.buckets.length}
        hiddenGroupCount={hiddenGroupCount}
      />

      <TimingBandsChart
        bands={bands}
        groups={shownGroups}
        bucketMs={bucketMs}
        hours={hours}
      />

      <LegDistributionChart rows={distribution} groupBy={groupBy} />

      <QualityTrendChart
        buckets={quality.buckets}
        groups={shownGroups}
        scoredRuns={quality.scoredRuns}
        bucketMs={bucketMs}
        hours={hours}
      />

      <h3 style={{ margin: '2rem 0 0.75rem', fontSize: '1rem' }}>Per-job exact values</h3>
      <p style={captionStyle}>
        The same window as the charts above, as numbers. A dash is not zero: it means the
        measurement was not recorded.
      </p>
      <JobStatsTable jobs={jobs} stats={stats} />
    </div>
  )
}

// ------------------------------------------------------------------- controls

function ChartControls({
  jobs, groupBy, onGroupBy, filters, onFilters,
}: {
  jobs: Job[]
  groupBy: GroupBy
  onGroupBy: (value: GroupBy) => void
  filters: ChartFilters
  onFilters: (value: ChartFilters) => void
}) {
  const providers = useMemo(() => providerOptions(jobs), [jobs])
  const methods = useMemo(() => methodOptions(jobs), [jobs])

  return (
    <div style={controlsStyle}>
      <div>
        <label htmlFor="reports-groupby" style={controlLabelStyle}>Group by</label>
        <select
          id="reports-groupby"
          value={groupBy}
          onChange={e => onGroupBy(e.target.value as GroupBy)}
          style={selectStyle}
        >
          <option value="none">Everything together</option>
          <option value="provider">Provider (outbound → inbound)</option>
          <option value="method">Method (outbound → inbound)</option>
          <option value="provider-method">Provider and method</option>
        </select>
      </div>
      <div>
        <label htmlFor="reports-provider" style={controlLabelStyle}>Provider</label>
        <select
          id="reports-provider"
          value={filters.provider}
          onChange={e => onFilters({ ...filters, provider: e.target.value })}
          style={selectStyle}
        >
          <option value="">All providers</option>
          {providers.map(value => (
            <option key={value} value={value}>
              {value === NOT_RECORDED ? NOT_RECORDED : providerLabel(value)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="reports-method" style={controlLabelStyle}>Method</label>
        {/*
          THE NEUTRAL LABEL IS REQUIRED HERE, not a shortcut. This list holds raw stored method
          values drawn from BOTH legs across every provider, and a stored method value is shared
          between brands — `eapi` means one vendor's API on one job and another's on the next.
          A brand-qualified label (`outboundMethodLabel`) would therefore name whichever vendor
          happened to be listed first, which is the defect
          tests/test_method_label_is_provider_qualified.py exists to stop. `fallbackMethodLabel`
          shows the stored value itself, which is the only honest label for a cross-provider
          filter option.
        */}
        <select
          id="reports-method"
          value={filters.method}
          onChange={e => onFilters({ ...filters, method: e.target.value })}
          style={selectStyle}
        >
        {/*
          THIS OPTION SHOWS THE STORED VALUE ITSELF, and calls no label helper. That is
          deliberate and it is not a way around
          tests/test_method_label_is_provider_qualified.py — it is that test's rule taken to its
          conclusion. A stored method value is shared between brands (`eapi` means one vendor's
          API on one job and another's on the next), so a method label is only meaningful when a
          provider qualifies it. This list spans BOTH legs and EVERY provider, so there is no
          single provider to qualify it with; any brand-bearing label would therefore name
          whichever vendor happened to be listed first. The stored value names no vendor at all,
          which is the only honest label available here, and an operator filtering by `imap` can
          act on `imap`.
        */}
        <option value="">All methods</option>
          {methods.map(value => (
            <option key={value} value={value}>
              {value === NOT_RECORDED ? NOT_RECORDED : value.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
      <p style={{ ...captionStyle, flexBasis: '100%', margin: 0 }}>
        A job may name a different provider or method on each leg, so a group is written
        “outbound → inbound” and is never collapsed to one value. A filter keeps a job when
        EITHER of its legs matches. An unconfigured leg is its own group, {NOT_RECORDED} — it is
        never counted as the default provider, because an inference must not be drawn as a
        measurement.
      </p>
    </div>
  )
}

/** Says what the charts below are and are NOT showing. Never silently truncates. */
function DataScopeNotice({
  window, runsError, emptyBuckets, bucketCount, hiddenGroupCount,
}: {
  window: LoadedWindow
  runsError: string | null
  emptyBuckets: number
  bucketCount: number
  hiddenGroupCount: number
}) {
  const notes: string[] = []
  if (window.truncated) {
    notes.push(
      `Charts cover the most recent ${window.fetched} of ${window.total} transmissions in this ` +
      `window — the rest were not loaded, so the earliest part of the period is INCOMPLETE.`,
    )
  }
  if (emptyBuckets > 0 && bucketCount > 0) {
    notes.push(
      `${emptyBuckets} of ${bucketCount} intervals contain no transmissions. They are drawn as ` +
      `gaps, not as zero — no line or bar is drawn across them.`,
    )
  }
  if (hiddenGroupCount > 0) {
    notes.push(`${hiddenGroupCount} further group(s) match but are not drawn; narrow the filters to see them.`)
  }

  if (runsError) {
    return (
      <div style={{ ...noticeStyle, borderColor: theme.chart.reference }} role="alert">
        Charts could not load transmissions for this window ({runsError}). The table below reads a
        different endpoint and is unaffected.
      </div>
    )
  }
  if (notes.length === 0) return null
  return (
    <div style={noticeStyle} role="note">
      {notes.map(note => <div key={note}>{note}</div>)}
    </div>
  )
}

// ------------------------------------------------------------------- chart 1

/**
 * End-to-end timing over the window, split into outbound and inbound BANDS.
 *
 * Stacked bars rather than a stacked area, deliberately. An interval with no transmissions has
 * NO BAR, which cannot be read as anything but absence; an area chart would have to be broken
 * open at that point and a break in a filled band is easy to misread as a low value. The two
 * bands stack, so their combined height is the end-to-end duration while each leg stays
 * separately visible — which is what makes "the inbound leg degraded on Tuesday" legible.
 *
 * Median per interval, not mean: one pathological transmission should not redraw the trend.
 */
function TimingBandsChart({
  bands, groups, bucketMs, hours,
}: {
  bands: ReturnType<typeof buildTimeBands>
  groups: string[]
  bucketMs: number
  hours: number
}) {
  const data = bands.buckets.map(bucket => {
    const row: Record<string, string | number | null> = { bucketStart: bucket.bucketStart }
    for (const group of groups) {
      const cell = bucket.legs[group]
      row[`${group}__outbound`] = cell?.outbound ?? null
      row[`${group}__inbound`] = cell?.inbound ?? null
      row[`${group}__n`] = cell?.n ?? 0
    }
    return row
  })

  const measured = bands.buckets.filter(b => groups.some(g => (b.legs[g]?.n ?? 0) > 0)).length

  return (
    <ChartCard
      title="End-to-end timing over time, by leg"
      caption={
        `Median seconds per ${describeBucket(bucketMs)}. The lower band is the outbound leg ` +
        `(submit → provider reported the fax sent); the upper, hatched band is the inbound leg ` +
        `(sent → received). Their combined height is the end-to-end duration. An interval with ` +
        `no transmissions has no bar.`
      }
      ariaLabel={
        `Stacked bar chart of median outbound and inbound leg seconds per ` +
        `${describeBucket(bucketMs)} over the last ${hours} hours. ${measured} of ` +
        `${bands.buckets.length} intervals contain measurements; the rest are empty and are ` +
        `drawn as gaps. Exact values are in the per-job table further down this page.`
      }
      empty={measured === 0}
      emptyMessage="No transmission in this window has both a submit and a sent time, so no leg duration can be drawn."
    >
      <BarChart data={data} margin={CHART_MARGIN}>
        <defs>{groups.map((group, index) => <HatchPattern key={group} group={group} index={index} />)}</defs>
        <CartesianGrid stroke={theme.chart.grid} vertical={false} />
        <XAxis
          dataKey="bucketStart"
          tickFormatter={value => formatBucketTick(value as string, bucketMs)}
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
        />
        <YAxis
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
          label={{ value: 'seconds', angle: -90, position: 'insideLeft', fill: theme.chart.axis, fontSize: 11 }}
        />
        <Tooltip content={<BandTooltip groups={groups} bucketMs={bucketMs} />} />
        <Legend wrapperStyle={legendStyle} />
        {groups.map(group => [
          <Bar
            key={`${group}-outbound`}
            dataKey={`${group}__outbound`}
            stackId={group}
            name={`${seriesPrefix(group)}outbound leg`}
            fill={theme.chart.outbound}
            stroke={theme.chart.outbound}
            // A stacked segment with a null value renders NOTHING, which is the gap rule for
            // a bar chart: absence of a bar, never a zero-height bar at the axis.
            isAnimationActive={false}
          />,
          <Bar
            key={`${group}-inbound`}
            dataKey={`${group}__inbound`}
            stackId={group}
            name={`${seriesPrefix(group)}inbound leg (hatched)`}
            fill={hatchFill(group, 'inbound')}
            stroke={theme.chart.inbound}
            isAnimationActive={false}
          />,
        ])}
      </BarChart>
    </ChartCard>
  )
}

// ------------------------------------------------------------------- chart 2

/**
 * Per-leg distribution across the groups the operator asked for.
 *
 * p50 and p90 side by side per leg, because a median alone hides the tail an SLA is judged on.
 * The sample count is on the axis label: a p90 over one sample IS that one sample, and the
 * reader has to be able to see that rather than take it for a distribution. A group with no
 * measured sample on either leg is absent from the chart entirely.
 */
function LegDistributionChart({
  rows, groupBy,
}: {
  rows: ReturnType<typeof buildLegDistribution>
  groupBy: GroupBy
}) {
  const data = rows.map(row => ({
    group: row.group,
    label: groupBy === 'none' ? 'All transmissions' : row.group,
    outbound_p50: row.outbound.p50,
    outbound_p90: row.outbound.p90,
    inbound_p50: row.inbound.p50,
    inbound_p90: row.inbound.p90,
    n_outbound: row.outbound.n,
    n_inbound: row.inbound.n,
  }))

  const thin = rows.filter(r => (r.outbound.n > 0 && r.outbound.n < 5) || (r.inbound.n > 0 && r.inbound.n < 5))

  return (
    <ChartCard
      title="Per-leg distribution by group"
      caption={
        `Median and 90th percentile seconds for each leg. The outbound bars are solid, the ` +
        `inbound bars hatched; the paler bar of each pair is the 90th percentile. ` +
        (thin.length > 0
          ? `${thin.length} group(s) have fewer than five measured samples on a leg — their ` +
            `percentiles describe those few transmissions and are not a distribution.`
          : `The sample count for each group is in its axis label.`)
      }
      ariaLabel={
        `Grouped bar chart comparing median and 90th percentile seconds for the outbound and ` +
        `inbound legs across ${rows.length} group(s). Each group's sample count is given in its ` +
        `axis label. Groups with no measured sample are omitted rather than drawn as zero.`
      }
      empty={rows.length === 0}
      emptyMessage="No group in this window has a measured leg duration."
    >
      <BarChart data={data} margin={CHART_MARGIN}>
        <defs>
          <HatchPattern group="dist" index={1} />
          <HatchPattern group="dist-p90" index={1} faded />
        </defs>
        <CartesianGrid stroke={theme.chart.grid} vertical={false} />
        <XAxis
          dataKey="label"
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
          tickFormatter={(label: string) => {
            const row = data.find(d => d.label === label)
            return row ? `${label} (n=${Math.max(row.n_outbound, row.n_inbound)})` : label
          }}
          interval={0}
          height={56}
        />
        <YAxis
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
          label={{ value: 'seconds', angle: -90, position: 'insideLeft', fill: theme.chart.axis, fontSize: 11 }}
        />
        <Tooltip content={<DistributionTooltip />} />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="outbound_p50" name="outbound median" fill={theme.chart.outbound} isAnimationActive={false} />
        <Bar dataKey="outbound_p90" name="outbound p90" fill={theme.chart.outbound} fillOpacity={0.45} stroke={theme.chart.outbound} isAnimationActive={false} />
        <Bar dataKey="inbound_p50" name="inbound median (hatched)" fill="url(#hatch-dist)" stroke={theme.chart.inbound} isAnimationActive={false} />
        <Bar dataKey="inbound_p90" name="inbound p90 (hatched)" fill="url(#hatch-dist-p90)" stroke={theme.chart.inbound} isAnimationActive={false} />
      </BarChart>
    </ChartCard>
  )
}

// ------------------------------------------------------------------- chart 3

/**
 * Quality score trend.
 *
 * `connectNulls={false}` IS THE POINT OF THIS CHART'S CONFIGURATION, not a detail. A straight
 * segment drawn between two scored intervals a fortnight apart reads as steady quality across
 * that fortnight, when in fact nothing was measured in it. The line breaks instead, and the
 * dots mark the intervals that hold a real reading.
 *
 * An interval where quality checking was OFF has no score, which is `null` and not `0` — zero
 * is the WORST possible score, so drawing "not checked" at the bottom of the axis would invent
 * the most alarming reading the chart is capable of displaying.
 */
function QualityTrendChart({
  buckets, groups, scoredRuns, bucketMs, hours,
}: {
  buckets: ReturnType<typeof buildQualityTrend>['buckets']
  groups: string[]
  scoredRuns: number
  bucketMs: number
  hours: number
}) {
  const data = buckets.map(bucket => {
    const row: Record<string, string | number | null> = { bucketStart: bucket.bucketStart }
    for (const group of groups) {
      row[`${group}__score`] = bucket.scores[group]?.score ?? null
      row[`${group}__n`] = bucket.scores[group]?.n ?? 0
    }
    return row
  })

  return (
    <ChartCard
      title="Quality score trend"
      caption={
        `Mean OCR quality score per ${describeBucket(bucketMs)}, 0–100. The line BREAKS across ` +
        `intervals with no scored transmission rather than joining across them: an unscored ` +
        `period is not a measurement, and 0 is the worst possible score rather than the absence ` +
        `of one. ${scoredRuns} transmission(s) in this window carry a score.`
      }
      ariaLabel={
        `Line chart of mean quality score, 0 to 100, per ${describeBucket(bucketMs)} over the ` +
        `last ${hours} hours, across ${groups.length} group(s). The line is broken over ` +
        `intervals with no scored transmission. ${scoredRuns} transmissions in this window are ` +
        `scored.`
      }
      empty={scoredRuns === 0}
      emptyMessage="No transmission in this window carries a quality score, so there is no quality trend to draw."
    >
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid stroke={theme.chart.grid} vertical={false} />
        <XAxis
          dataKey="bucketStart"
          tickFormatter={value => formatBucketTick(value as string, bucketMs)}
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
        />
        <YAxis
          domain={[0, 100]}
          stroke={theme.chart.axis}
          tick={{ fill: theme.chart.axis, fontSize: 11 }}
          label={{ value: 'score', angle: -90, position: 'insideLeft', fill: theme.chart.axis, fontSize: 11 }}
        />
        <Tooltip content={<QualityTooltip groups={groups} bucketMs={bucketMs} />} />
        <Legend wrapperStyle={legendStyle} />
        {groups.map((group, index) => (
          <Line
            key={group}
            type="linear"
            dataKey={`${group}__score`}
            name={`${seriesPrefix(group)}mean quality score`}
            stroke={theme.chart.quality}
            strokeDasharray={index === 0 ? theme.chart.dash.quality : `${4 + index * 2} ${3 + index}`}
            strokeWidth={2}
            dot={{ r: 3, fill: theme.chart.quality, stroke: theme.chart.quality }}
            // THE GAP RULE. Do not remove: a joined line across an unmeasured period reports
            // steady quality that was never measured.
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartCard>
  )
}

// ------------------------------------------------------------------- shared chart chrome

const CHART_MARGIN = { top: 8, right: 16, bottom: 8, left: 8 }

function ChartCard({
  title, caption, ariaLabel, empty, emptyMessage, children,
}: {
  title: string
  caption: string
  ariaLabel: string
  empty: boolean
  emptyMessage: string
  children: React.ReactElement
}) {
  return (
    <section style={cardStyle}>
      <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem' }}>{title}</h3>
      <p style={captionStyle}>{caption}</p>
      {empty ? (
        <p style={{ ...captionStyle, padding: '2rem 0', textAlign: 'center' }}>{emptyMessage}</p>
      ) : (
        <div role="img" aria-label={ariaLabel} style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      )}
    </section>
  )
}

/**
 * The hatch that carries series identity WITHOUT colour.
 *
 * Index 0 is unhatched on purpose: with one group the chart should be as plain as possible, and
 * the inbound band is still distinguished from outbound by its own hatch (index 1's angle).
 */
function HatchPattern({ group, index, faded }: { group: string; index: number; faded?: boolean }) {
  const angle = HATCH_ANGLES[index % HATCH_ANGLES.length]
  return (
    <pattern
      id={`hatch-${group}`}
      patternUnits="userSpaceOnUse"
      width={6}
      height={6}
      patternTransform={angle === null ? undefined : `rotate(${angle})`}
    >
      <rect width={6} height={6} fill={theme.chart.inbound} fillOpacity={faded ? 0.45 : 1} />
      <line x1={0} y1={0} x2={0} y2={6} stroke={theme.bg} strokeWidth={2} />
    </pattern>
  )
}

function hatchFill(group: string, leg: 'outbound' | 'inbound'): string {
  if (leg === 'outbound') return theme.chart.outbound
  return `url(#hatch-${group})`
}

function seriesPrefix(group: string): string {
  return group === ALL_GROUP ? '' : `${group} — `
}

function describeBucket(bucketMs: number): string {
  if (bucketMs < 3600_000) return `${bucketMs / 60_000} minutes`
  if (bucketMs === 3600_000) return 'hour'
  if (bucketMs < 86_400_000) return `${bucketMs / 3600_000} hours`
  return 'day'
}

function formatBucketTick(iso: string, bucketMs: number): string {
  return bucketMs >= 86_400_000 ? fmtDate(iso) : fmtTime(iso)
}

interface TooltipProps {
  active?: boolean
  label?: string | number
  payload?: Array<{ dataKey?: string | number; name?: string; value?: number | string | null }>
}

/** Names every series in WORDS and says "no measurement" rather than showing nothing. */
function BandTooltip({ groups, bucketMs, active, label, payload }: TooltipProps & { groups: string[]; bucketMs: number }) {
  if (!active || label === undefined) return null
  const row = payload?.[0] as unknown as { payload?: Record<string, number | null> } | undefined
  const values = row?.payload ?? {}
  return (
    <div style={tooltipStyle}>
      <div style={{ marginBottom: '0.35rem' }}>{fmtDateTime(String(label))} · {describeBucket(bucketMs)} interval</div>
      {groups.map(group => {
        const outbound = values[`${group}__outbound`]
        const inbound = values[`${group}__inbound`]
        const n = values[`${group}__n`] ?? 0
        return (
          <div key={group} style={{ marginBottom: '0.25rem' }}>
            {group !== ALL_GROUP && <div style={{ color: theme.textMuted }}>{group}</div>}
            <div>outbound leg: {outbound === null || outbound === undefined ? 'no measurement' : `${outbound}s`}</div>
            <div>inbound leg: {inbound === null || inbound === undefined ? 'no measurement' : `${inbound}s`}</div>
            <div style={{ color: theme.textMuted }}>{n} transmission(s) entered in this interval</div>
          </div>
        )
      })}
    </div>
  )
}

function DistributionTooltip({ active, payload, label }: TooltipProps) {
  if (!active) return null
  const row = payload?.[0] as unknown as { payload?: Record<string, number | string | null> } | undefined
  const values = row?.payload
  if (!values) return null
  const cell = (v: number | string | null | undefined) => (v === null || v === undefined ? 'no measurement' : `${v}s`)
  return (
    <div style={tooltipStyle}>
      <div style={{ marginBottom: '0.35rem' }}>{String(label)}</div>
      <div>outbound median: {cell(values.outbound_p50)} · p90: {cell(values.outbound_p90)}</div>
      <div>inbound median: {cell(values.inbound_p50)} · p90: {cell(values.inbound_p90)}</div>
      <div style={{ color: theme.textMuted }}>
        measured samples — outbound {values.n_outbound ?? 0}, inbound {values.n_inbound ?? 0}
      </div>
    </div>
  )
}

function QualityTooltip({ groups, bucketMs, active, label, payload }: TooltipProps & { groups: string[]; bucketMs: number }) {
  if (!active || label === undefined) return null
  const row = payload?.[0] as unknown as { payload?: Record<string, number | null> } | undefined
  const values = row?.payload ?? {}
  return (
    <div style={tooltipStyle}>
      <div style={{ marginBottom: '0.35rem' }}>{fmtDateTime(String(label))} · {describeBucket(bucketMs)} interval</div>
      {groups.map(group => {
        const score = values[`${group}__score`]
        const n = values[`${group}__n`] ?? 0
        return (
          <div key={group}>
            {group !== ALL_GROUP && <span style={{ color: theme.textMuted }}>{group}: </span>}
            {score === null || score === undefined
              ? 'not scored in this interval'
              : `mean score ${score} over ${n} transmission(s)`}
          </div>
        )
      })}
    </div>
  )
}

// ------------------------------------------------------------------- table

function JobStatsTable({ jobs, stats }: { jobs: Job[]; stats: Record<string, JobStats> }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
          <th style={thStyle}>Job</th>
          <th style={thStyle}>Samples</th>
          <th style={thStyle}>Success %</th>
          <th style={thStyle}>p50</th>
          <th style={thStyle}>p90</th>
          <th style={thStyle}>p99</th>
          <th style={thStyle}>Min</th>
          <th style={thStyle}>Max</th>
        </tr>
      </thead>
      <tbody>
        {jobs.map(job => {
          const s = stats[job.id]
          return (
            <tr key={job.id} style={{ borderTop: `1px solid ${theme.border}` }}>
              <td style={tdStyle}>{job.name}</td>
              <td style={tdStyle}>{s?.sample_count ?? '—'}</td>
              <td style={tdStyle}>{s?.success_rate_percent != null ? `${s.success_rate_percent}%` : '—'}</td>
              <td style={tdStyle}>{s?.p50_seconds != null ? `${s.p50_seconds}s` : '—'}</td>
              <td style={tdStyle}>{s?.p90_seconds != null ? `${s.p90_seconds}s` : '—'}</td>
              <td style={tdStyle}>{s?.p99_seconds != null ? `${s.p99_seconds}s` : '—'}</td>
              <td style={tdStyle}>{s?.min_seconds != null ? `${s.min_seconds}s` : '—'}</td>
              <td style={tdStyle}>{s?.max_seconds != null ? `${s.max_seconds}s` : '—'}</td>
            </tr>
          )
        })}
        {jobs.length === 0 && (
          <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No active jobs</td></tr>
        )}
      </tbody>
    </table>
  )
}

// ------------------------------------------------------------------- styles

const headerRowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem',
}

const controlsStyle: React.CSSProperties = {
  display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end',
  background: theme.bgAlt, border: `1px solid ${theme.border}`,
  borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem',
}

const controlLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem',
}

const srOnlyLabel: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
}

const selectStyle: React.CSSProperties = {
  padding: '0.4rem', background: theme.bg, color: theme.text,
  border: `1px solid ${theme.border}`, borderRadius: '4px',
}

const buttonStyle: React.CSSProperties = {
  padding: '0.4rem 1rem', border: `1px solid ${theme.primary}`, color: theme.primary,
  borderRadius: '4px', cursor: 'pointer', background: 'transparent',
}

const cardStyle: React.CSSProperties = {
  background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: '8px',
  padding: '1rem', marginBottom: '1.25rem',
}

const captionStyle: React.CSSProperties = {
  margin: '0 0 0.75rem', fontSize: '0.8rem', color: theme.textMuted, lineHeight: 1.5,
}

const noticeStyle: React.CSSProperties = {
  background: theme.bgAlt, border: `1px solid ${theme.chart.axis}`, borderRadius: '8px',
  padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: theme.text,
  display: 'flex', flexDirection: 'column', gap: '0.35rem',
}

const legendStyle: React.CSSProperties = { fontSize: '0.75rem', color: theme.text }

const tooltipStyle: React.CSSProperties = {
  background: theme.chart.tooltipBg, border: `1px solid ${theme.border}`, borderRadius: '6px',
  padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: theme.text,
}

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', background: theme.bgAlt,
  borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
}

const thStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: theme.textMuted }
const tdStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', fontSize: '0.85rem' }
